import React from 'react';
import PropTypes from 'prop-types';
import GlassIcon from '../ui/GlassIcon';
import { useTranslation } from '../../i18n/LanguageContext';

const TILE_SURFACE = 'tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-[1.4rem] tw-border tw-bg-white/70 tw-p-3.5 tw-text-left tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_12px_32px_-24px_rgba(15,23,42,0.4)] sm:tw-p-5 [@media(max-height:820px)]:tw-py-3 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_32px_-24px_rgba(0,0,0,0.9)]';

// A tile with onClick is a quick filter; the selected one gets a teal rim
const TILE_BORDER = (active) => (active
  ? 'tw-border-teal-500/50 tw-ring-2 tw-ring-teal-500/20 dark:tw-border-teal-400/40'
  : 'tw-border-white/80 dark:tw-border-white/10');

const StatTile = ({ label, shortLabel, value, format, suffix, hint, share, shareTitle, icon: Icon, gradient, barClass, ready, locale, onClick, active }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button', onClick, 'aria-pressed': Boolean(active) } : {})}
      className={`${TILE_SURFACE} ${TILE_BORDER(active)} ${onClick ? 'tw-transition-[border-color,box-shadow,transform] tw-duration-200 hover:tw-bg-white active:tw-scale-[0.99] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.06]' : ''}`}
    >
      <div className="tw-flex tw-items-center tw-gap-3">
        <GlassIcon gradient={gradient} size="xs" className="tw-hidden sm:tw-grid">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <div className="tw-min-w-0">
          {ready ? (
            <p className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-slate-900 tw-tabular-nums sm:tw-text-3xl dark:tw-text-white">
              {format ? format(value) : value.toLocaleString(locale)}
              {suffix && <span className="tw-ml-1 tw-text-base tw-font-medium tw-text-slate-400 sm:tw-text-lg dark:tw-text-slate-500">{suffix}</span>}
            </p>
          ) : (
            <span aria-hidden="true" className="tw-block tw-h-7 tw-w-14 tw-animate-pulse tw-rounded-lg tw-bg-slate-200/80 sm:tw-h-8 dark:tw-bg-white/10" />
          )}
          <p className="tw-mt-1.5 tw-truncate tw-text-xs tw-font-medium tw-text-slate-500 sm:tw-text-sm dark:tw-text-slate-400">
            {/* Phones show the short form; three tiles share the width there */}
            <span className="sm:tw-hidden">{shortLabel || label}</span>
            <span className="tw-hidden sm:tw-inline">{label}</span>
          </p>
        </div>
        {hint && (
          <span className="tw-ml-auto tw-hidden tw-shrink-0 tw-self-start tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-tabular-nums sm:tw-inline-flex dark:tw-bg-white/[0.07] dark:tw-text-slate-400">
            {hint}
          </span>
        )}
      </div>

      {share !== undefined && (
        <div className="tw-flex tw-items-center tw-gap-2 [@media(max-height:820px)]:tw-hidden" title={shareTitle}>
          <span className="tw-h-1.5 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
            <span
              className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-700 tw-ease-out ${barClass}`}
              style={{ width: `${Math.round(share * 100)}%` }}
            />
          </span>
          <span className="tw-hidden tw-text-xs tw-font-semibold tw-text-slate-500 tw-tabular-nums sm:tw-inline dark:tw-text-slate-400">
            {new Intl.NumberFormat(locale, { style: 'percent' }).format(share)}
          </span>
        </div>
      )}
    </Tag>
  );
};

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  shortLabel: PropTypes.string,
  value: PropTypes.number.isRequired,
  format: PropTypes.func,
  suffix: PropTypes.string,
  hint: PropTypes.string,
  share: PropTypes.number,
  shareTitle: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  barClass: PropTypes.string,
  ready: PropTypes.bool,
  locale: PropTypes.string,
  onClick: PropTypes.func,
  active: PropTypes.bool,
};

/**
 * Head-count tiles of a data page (e.g. total / active / inactive). A tile
 * with `share` (0–1) also shows its part of the total as a bar; one with
 * `onClick` works as a quick filter (`active` marks the current one).
 * `format(value)` replaces the default number format (e.g. amounts with two
 * decimals) and `suffix` is drawn small after the value (e.g. "₺"); `hint` is a
 * small note in the corner (e.g. "12 talep", hidden on phones).
 * `className` sets the grid columns (three tiles in a row by default).
 */
const StatTiles = ({ tiles, ready, className = 'tw-grid-cols-3' }) => {
  const { locale } = useTranslation();
  return (
    <div className={`tw-grid tw-gap-2.5 sm:tw-gap-4 ${className}`}>
      {tiles.map(({ key, ...tile }) => (
        <StatTile key={key} {...tile} ready={ready} locale={locale} />
      ))}
    </div>
  );
};

StatTiles.propTypes = {
  tiles: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired,
  ready: PropTypes.bool,
  className: PropTypes.string,
};

export default StatTiles;
